import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { AdminService, UserSummary, ResetPasswordResponse, PaginatedUsersResponse } from '../../services/admin.service';

@Component({
  selector: 'app-admin-dashboard',
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.scss']
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
  users: UserSummary[] = [];
  loading = false;
  errorMessage = '';
  successMessage = '';

  // Pagination
  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  totalPages = 0;
  pageSizeOptions = [5, 10, 25, 50];

  // Search
  searchTerm = '';
  private searchSubject = new Subject<string>();

  constructor(private adminService: AdminService) {}

  ngOnInit(): void {
    // Debounced search pipeline
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(term => {
        this.currentPage = 0; // reset to first page on new search
        return this.loadUsers(term);
      })
    ).subscribe({
      next: (response) => this.updateUsers(response),
      error: (err) => this.handleError(err)
    });

    // ✅ TRIGGER INITIAL LOAD
    this.searchSubject.next('');
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  /**
   * Calls the API and returns Observable. Sets loading=true.
   */
  private loadUsers(search?: string) {
    this.loading = true;
    this.errorMessage = '';
    return this.adminService.getPaginatedUsers(this.currentPage, this.pageSize, search).pipe(
      catchError(err => {
        this.handleError(err);
        throw err; // rethrow to keep observable contract
      })
    );
  }

  private updateUsers(response: PaginatedUsersResponse): void {
    this.users = response.content;
    this.totalElements = response.totalElements;
    this.totalPages = response.totalPages;
    this.currentPage = response.currentPage; // keep in sync with backend
    this.loading = false;
  }

  private handleError(err: any): void {
    this.errorMessage = err.message || 'Failed to load users';
    this.loading = false;
    console.error('AdminDashboard error:', err);
  }

  // Pagination actions
  loadPage(page: number): void {
    if (page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
    this.loadUsers(this.searchTerm).subscribe({
      next: (response) => this.updateUsers(response),
      error: (err) => this.handleError(err)
    });
  }

  onPageSizeChange(): void {
    this.currentPage = 0;
    this.loadUsers(this.searchTerm).subscribe({
      next: (response) => this.updateUsers(response),
      error: (err) => this.handleError(err)
    });
  }

  // Search input handler
  onSearchInput(term: string): void {
    this.searchTerm = term;
    this.searchSubject.next(term);
  }

  // Reload current page after actions
  reloadCurrentPage(): void {
    this.loadUsers(this.searchTerm).subscribe({
      next: (response) => this.updateUsers(response),
      error: (err) => this.handleError(err)
    });
  }

  // Admin actions
  resetPassword(user: UserSummary): void {
    if (!confirm(`Reset password for ${user.name} (${user.email})?`)) return;
    this.loading = true;
    this.adminService.resetPassword(user.id).subscribe({
      next: (response: ResetPasswordResponse) => {
        this.successMessage = `Password reset for ${response.userName}. New password: ${response.newPassword}`;
        this.reloadCurrentPage();
      },
      error: (err) => {
        this.errorMessage = err.message || 'Reset failed';
        this.loading = false;
      }
    });
  }

  toggleUserStatus(user: UserSummary): void {
    const action = user.active ? 'deactivate' : 'reactivate';
    if (!confirm(`${action} ${user.name}?`)) return;
    this.loading = true;
    const request = user.active
      ? this.adminService.deactivateUser(user.id)
      : this.adminService.reactivateUser(user.id);
    request.subscribe({
      next: (response: string) => {
        this.successMessage = response;
        this.reloadCurrentPage();
      },
      error: (err) => {
        this.errorMessage = err.message || `${action} failed`;
        this.loading = false;
      }
    });
  }

  clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }
}